import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faClone,
} from '@fortawesome/pro-regular-svg-icons';
import { Toggle } from '@client/components/common/inputs/Toggle';
import { useThenableModal } from '@client/components/common/Modal';
import { HideWarningModal } from '../Modals';
import { ColorPicker } from '../ColorPicker';
import { useFocusWithin } from 'react-aria';
import { AddSection } from './AddSection';
import { DeleteSection } from './DeleteSection';
import { HeroSelectModal } from './HeroSelectModal';
import { ClickableIcon } from '@client/new/components/basic';
import classNames from 'classnames';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { ISection } from '@playbook-technologies/common-schemas';
import { SecondaryButton } from '@client/new/components/button';

export const Container = ({
  children,
  title,
  alwaysEnabled,
  enabled,
  onChangeEnabled,
  onMove,
  colors,
  color,
  onColorIndexChange,
  colorSelectEnabled = true,
  canMoveUp = false,
  canMoveDown = false,
  onAddSection,
  onDeleteSection,
  onSwitchHeader,
  onDuplicateSection,
}: {
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  color: string;
  colors: string[];
  children: React.ReactNode;
  title: string;
  alwaysEnabled?: boolean;
  enabled?: boolean;
  onChangeEnabled?: (val: boolean) => void;
  onColorIndexChange?: (val: number) => void;
  colorSelectEnabled?: boolean;
  onMove?: (by: number) => void;
  onAddSection?: (s: ISection['type']) => void;
  onDeleteSection?: () => void;
  onDuplicateSection?: () => void;
  onSwitchHeader?: (o: ISection['type']) => void;
}) => {
  const [folded, setFolded] = useState(!enabled);
  const hideWarningModal = useThenableModal(HideWarningModal);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      setFolded(false);
    },
  });

  const handleHideToggleChange = async (val: boolean) => {
    if (val) {
      setFolded(false);
      return onChangeEnabled && onChangeEnabled(true);
    }

    const choice = await hideWarningModal.open();
    if (choice) {
      setFolded(true);
      return onChangeEnabled && onChangeEnabled(false);
    }
  };

  const handleColorChange = (val: string) => {
    const index = colors.findIndex((c) => c === val);
    onColorIndexChange && onColorIndexChange(index);
  };

  return (
    <>
      <div className="bg-white mb-6 rounded-lg" {...focusWithinProps}>
        <div
          className={`
          w-full flex flex-wrap bg-[#E1E5F0] items-center justify-between lg:h-[70px]
          ${folded ? 'rounded-lg' : 'rounded-t-lg'}
        `}
        >
          <div className="flex items-center w-full md:w-auto h-[70px] justify-between px-2.5">
            <h3 className="md:pl-[10px] m-0 font-[800] text-[18px] 2xl:text-h24 text-gray-1 text-ellipsis whitespace-nowrap max-w-[200px] 2xl:max-w-none overflow-clip">
              {title}
            </h3>
            <div className="md:hidden">
              <ColorPicker
                disableHex
                disableLabels
                value={color || '#FFFFFF'}
                onChange={handleColorChange}
                error={false}
                colors={colors}
                borderColor="#6367EF"
              />
            </div>
          </div>
          <div className="hidden md:flex flex-wrap items-center h-full">
            {colorSelectEnabled && (
              <>
                <div className="ml-8 mr-8 flex items-center">
                  <div className="hidden md:block mr-4">Background: </div>
                  <ColorPicker
                    disableHex
                    disableLabels
                    value={color || '#FFFFFF'}
                    onChange={handleColorChange}
                    error={false}
                    colors={colors}
                    borderColor="#6367EF"
                  />
                </div>
                <div className="border-r border-white h-full" />
              </>
            )}
            {alwaysEnabled ? null : (
              <ActionBox>
                <Toggle
                  value={Boolean(enabled)}
                  onChange={handleHideToggleChange}
                />
              </ActionBox>
            )}
            <div className="border-r border-white h-full" />
            {onDuplicateSection && (
              <ActionBox>
                <ClickableIcon
                  title="Duplicate"
                  icon={faClone}
                  onClick={onDuplicateSection}
                />
              </ActionBox>
            )}
            {onDeleteSection && (
              <ActionBox>
                <DeleteSection onDelete={onDeleteSection} />
              </ActionBox>
            )}
            {onSwitchHeader && (
              <ActionBox>
                <SwitchHeader onSwitchHeader={onSwitchHeader} />
              </ActionBox>
            )}
            <ActionBox
              style={{
                opacity: canMoveUp ? 1 : 0.2,
                cursor: canMoveUp ? 'pointer' : 'default',
              }}
              onClick={() => canMoveUp && onMove && onMove(-1)}
            >
              <ClickableIcon icon={faArrowUp} title="Move up" />
            </ActionBox>
            <ActionBox
              style={{
                opacity: canMoveDown ? 1 : 0.2,
                cursor: canMoveDown ? 'pointer' : 'default',
              }}
              onClick={() => canMoveDown && onMove && onMove(1)}
            >
              <ClickableIcon icon={faArrowDown} title="Move down" />
            </ActionBox>
            <ActionBox onClick={() => setFolded((x) => !x)}>
              <ClickableIcon
                title={folded ? 'Expand' : 'Collapse'}
                icon={folded ? faChevronUp : faChevronDown}
              />
            </ActionBox>
          </div>

          <div className="w-full flex justify-end">
            <ToggleButtonGroup value={'left'} exclusive className="md:hidden">
              {onDeleteSection && (
                <ToggleButton value="left">
                  <DeleteSection onDelete={onDeleteSection} />
                </ToggleButton>
              )}
              {alwaysEnabled ? null : (
                <ToggleButton value="left">
                  <Toggle
                    value={Boolean(enabled)}
                    onChange={handleHideToggleChange}
                  />
                </ToggleButton>
              )}
              {onDuplicateSection && (
                <ToggleButton value="left">
                  <ClickableIcon
                    title="Duplicate"
                    icon={faClone}
                    onClick={onDuplicateSection}
                  />
                </ToggleButton>
              )}
              {onSwitchHeader && (
                <ToggleButton value="left">
                  <SwitchHeader onSwitchHeader={onSwitchHeader} />
                </ToggleButton>
              )}
              <ToggleButton
                value="left"
                onClick={() => canMoveUp && onMove && onMove(-1)}
              >
                <ClickableIcon icon={faArrowUp} title="Move up" />
              </ToggleButton>
              <ToggleButton
                value="left"
                onClick={() => canMoveDown && onMove && onMove(1)}
              >
                <ClickableIcon icon={faArrowDown} title="Move down" />
              </ToggleButton>
              <ToggleButton value="left" onClick={() => setFolded((x) => !x)}>
                <ClickableIcon
                  title={folded ? 'Expand' : 'Collapse'}
                  icon={folded ? faChevronUp : faChevronDown}
                />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>

        <div
          className={`${
            folded ? 'max-h-0 overflow-hidden' : 'block p-[30px] pt-[20px]'
          }`}
        >
          {children}
        </div>
      </div>
      <hideWarningModal.Popup />
      {onAddSection && (
        <div className="flex w-full my-6">
          <div className="flex flex-grow items-center">
            <div className="border-t-[1px] border-primary flex-grow" />
          </div>
          <div className="w-10 flex justify-center mx-6">
            <AddSection onAdd={onAddSection} />
          </div>
          <div className="flex flex-grow items-center">
            <div className="border-t-[1px] border-primary flex-grow" />
          </div>
        </div>
      )}
    </>
  );
};

function ActionBox({
  children,
  className,
  ...props
}: PropsWithChildren<
  React.HTMLAttributes<HTMLDivElement> & { className?: string }
>) {
  return (
    <>
      <div
        {...props}
        className={classNames(
          'flex w-[75px] items-center justify-center',
          className,
        )}
      >
        {children}
      </div>
      <div className="border-r border-white h-full" />
    </>
  );
}

export function SwitchHeader({
  onSwitchHeader,
}: {
  onSwitchHeader: (o: string) => void;
}) {
  const [show, setShow] = React.useState(false);
  const ref = useRef<any>(null);

  useEffect(() => {
    if (show && ref.current) {
      ref.current?.focus();
    }
  }, [show]);

  if (show) {
    return (
      <HeroSelectModal
        onClose={() => setShow(false)}
        onHeroSelect={onSwitchHeader}
      />
    );
  }
  return (
    <span className="text-xl cursor-pointer flex justify-center">
      <SecondaryButton
        className="max-w-[75px] h-[70px]"
        onClick={() => setShow(true)}
      >
        Change Hero
      </SecondaryButton>
    </span>
  );
}
